import type { init as sentryInit } from "@sentry/nextjs";

import {
  SUKHA_CHROME_EXTENSION_ID,
  SUKHA_SENTRY_CLIENT_WEB,
} from "@/lib/sentry/constants";

type WebSentryBeforeSend = NonNullable<
  Parameters<typeof sentryInit>[0]["beforeSend"]
>;
type WebSentryErrorEvent = Parameters<WebSentryBeforeSend>[0];

const FRAME_ANT_PATTERN = /\/frame_ant\/frame_ant\.js/;
const SEVENDATA_PATTERN = /sevendata\.fun/;

/** Exception `type` values that are transient/non-actionable client noise. */
const NOISE_EXCEPTION_TYPES = new Set(["AbortError"]);

/**
 * Reader-mode scripts that iOS browsers inject into the page.
 *
 * Brave and Firefox on iOS are both WebKit shells built from Firefox's iOS
 * source, and both inject content scripts that hang off a `__firefox__` global.
 * When one of those scripts runs before its global exists, it throws — inside
 * *their* code, on *our* page.
 *
 * Sentry reports it as ours anyway. The injected script is anonymous global
 * code, so WebKit has no file to blame and attributes the throw to the document
 * URL, which arrives as an in-app frame on the route the member was reading.
 * There is nothing to fix on our side and no stack that would help if there
 * were, so these only bury real errors: one iPhone produced 495 of them in
 * eight minutes.
 *
 * Matching the identifier is what keeps this narrow. Nothing we ship references
 * `__firefox__`, so its presence in a message means the throw came from browser
 * code, regardless of which form the failure took.
 */
const BROWSER_INJECTED_GLOBAL = /__firefox__/;
/**
 * Exception `value` patterns for transient noise:
 * - fetch aborted on navigation/unmount (AbortError message form)
 * - Clerk failing to reach or load its script (user network / CDN blips)
 */
const NOISE_MESSAGE_PATTERNS: RegExp[] = [
  /the user aborted a request/i,
  /ClerkJS: Network error/i,
  /Clerk:\s*Failed to load Clerk JS/i,
  /Failed to load script:.*clerk/i,
  BROWSER_INJECTED_GLOBAL,
];

/**
 * A DOM walker some page tool injected into the document.
 *
 * Extensions, translators and reader modes traverse the page by recursing over
 * a tree walker. When they hand `createTreeWalker` a node that does not have it
 * — a shadow root, a fragment — it throws, in *their* code, on *our* page. The
 * injected script is inline, so WebKit has no file to blame and attributes the
 * throw to the document URL, which reaches us as an in-app frame on whatever
 * route the member was on.
 *
 * Unlike `__firefox__`, the identifier here is standard DOM, so the message is
 * not enough on its own — see the document-frame check at the call site.
 */
const INJECTED_DOM_WALKER = /\bcreateTreeWalker is not a function/;

/**
 * A crypto-wallet extension's background worker.
 *
 * It posts dApp updates to every open page over a port, and throws
 * "Attempting to use a disconnected port object" when a page it was talking to
 * has gone away. Sentry records its frames as `app:///extensionServiceWorker.js`
 * and files the throw against whatever route the member was on — 67 events on
 * /calendar (THE-SUKHA-COMPANY-RP, -SC). Nothing we ship is served at that
 * path, so a frame there is someone else's code.
 */
const WALLET_SERVICE_WORKER_FRAME = /\/extensionServiceWorker\.js$/;

/**
 * Did this frame come from the HTML document rather than from a script file?
 *
 * Everything we ship is served as `app:///_next/static/chunks/*.js`. A frame
 * pointing at the document itself — `app:///`, `app:///calendar` — is code that
 * arrived without a file of its own, which on a page of ours means it was
 * injected into it.
 */
function isDocumentFrame(filename: string | undefined): boolean {
  return (
    typeof filename === "string" && filename.startsWith("app:///") && !filename.endsWith(".js")
  );
}

/**
 * Next.js telling us it already recovered from a failed RSC prefetch.
 *
 * The router prefetches the React payload for a link; if that fetch fails, it
 * logs this and navigates the old-fashioned way. The member sees a working
 * navigation. But the rejected promise still reaches
 * `window.onunhandledrejection`, so Sentry reports an unhandled `TypeError:
 * Load failed` for something the framework has already handled.
 */
const RSC_PREFETCH_RECOVERED =
  /Failed to fetch RSC payload .*Falling back to browser navigation/i;

/**
 * Generic fetch failures, by browser: Safari says "Load failed", Chrome says
 * "Failed to fetch".
 *
 * Matched **only** alongside the recovery breadcrumb above, never on their own.
 * These same two strings are what a genuinely broken API call produces, and
 * dropping them wholesale would hide real failures — which is the difference
 * between filtering noise and losing error reporting.
 */
const GENERIC_FETCH_FAILURE = /^(Load failed|Failed to fetch|NetworkError.*)$/i;

/** Did Next.js log, in this event's own breadcrumbs, that it recovered? */
function hasRecoveredRscPrefetch(event: WebSentryErrorEvent): boolean {
  const crumbs = event.breadcrumbs ?? [];
  return crumbs.some((crumb) => {
    if (crumb.category !== "console") return false;
    const args = (crumb.data as { arguments?: unknown[] } | undefined)?.arguments;
    const text = [crumb.message, ...(Array.isArray(args) ? args : [])]
      .filter((part): part is string => typeof part === "string")
      .join(" ");
    return RSC_PREFETCH_RECOVERED.test(text);
  });
}

function getExceptionFrames(event: WebSentryErrorEvent): Array<{ filename?: string }> {
  return event.exception?.values?.[0]?.stacktrace?.frames ?? [];
}

function analyzeChromeExtensionFrames(
  frames: Array<{ filename?: string }>
): {
  hasExtensionFrame: boolean;
  hasSukhaExtensionFrame: boolean;
} {
  let hasExtensionFrame = false;
  let hasSukhaExtensionFrame = false;
  const sukhaPrefix = `chrome-extension://${SUKHA_CHROME_EXTENSION_ID}/`;

  for (const frame of frames) {
    if (typeof frame.filename !== "string") {
      continue;
    }
    if (!frame.filename.includes("chrome-extension://")) {
      continue;
    }
    hasExtensionFrame = true;
    if (frame.filename.startsWith(sukhaPrefix)) {
      hasSukhaExtensionFrame = true;
    }
  }

  return { hasExtensionFrame, hasSukhaExtensionFrame };
}

function shouldDropKnownNoise(event: WebSentryErrorEvent): boolean {
  const frames = getExceptionFrames(event);
  if (
    frames.some(
      (f) =>
        typeof f.filename === "string" &&
        (FRAME_ANT_PATTERN.test(f.filename) || WALLET_SERVICE_WORKER_FRAME.test(f.filename))
    )
  ) {
    return true;
  }

  const firstException = event.exception?.values?.[0];
  const errorType = firstException?.type ?? "";
  const errorMessage = firstException?.value ?? "";
  const requestUrl = event.request?.url ?? "";
  if (SEVENDATA_PATTERN.test(errorMessage) || SEVENDATA_PATTERN.test(requestUrl)) {
    return true;
  }

  if (NOISE_EXCEPTION_TYPES.has(errorType)) {
    return true;
  }
  if (NOISE_MESSAGE_PATTERNS.some((pattern) => pattern.test(errorMessage))) {
    return true;
  }

  // A tree walker from an injected script. Both halves are required: the API
  // is standard DOM, so the same failure from one of our own chunks is our bug
  // and must still be reported — only a throw from the document is theirs.
  if (
    INJECTED_DOM_WALKER.test(errorMessage) &&
    frames.some((f) => isDocumentFrame(f.filename))
  ) {
    return true;
  }

  // A prefetch the router already fell back from. Both halves are required: a
  // bare "Load failed" is exactly what a real broken request looks like.
  if (
    errorType === "TypeError" &&
    GENERIC_FETCH_FAILURE.test(errorMessage) &&
    hasRecoveredRscPrefetch(event)
  ) {
    return true;
  }

  return false;
}

/**
 * Tags web events that involve Chrome extension stack frames instead of dropping them.
 * Drops only confirmed unrelated noise (frame_ant, sevendata.fun).
 */
export const applyWebSentryBeforeSend: WebSentryBeforeSend = (event, _hint) => {
  if (shouldDropKnownNoise(event)) {
    return null;
  }

  event.tags = {
    ...event.tags,
    "sukha.client": SUKHA_SENTRY_CLIENT_WEB,
  };

  const { hasExtensionFrame, hasSukhaExtensionFrame } = analyzeChromeExtensionFrames(
    getExceptionFrames(event)
  );

  if (hasExtensionFrame) {
    event.tags = {
      ...event.tags,
      stack_contains_chrome_extension: "true",
      stack_contains_sukha_extension: hasSukhaExtensionFrame ? "true" : "false",
      stack_contains_unknown_extension: hasSukhaExtensionFrame ? "false" : "true",
    };
  }

  return event;
};

/** denyUrls patterns retained for confirmed junk only (not all chrome-extension://). */
export const WEB_SENTRY_DENY_URLS: Array<string | RegExp> = [/\/frame_ant\/frame_ant\.js/];
